/**
 * directoryReturn — what the directory remembers for the way back.
 *
 * The directory's own state (query, view, filters, sort, paging depth)
 * lives in its URL, so the browser's back button restores it by itself.
 * Two things the URL cannot carry are kept here, per tab, in
 * sessionStorage:
 *
 *   - the LAST DIRECTORY SEARCH — the URL a detail page's "Directory"
 *     control returns to. History cannot answer this: a reloaded or
 *     deep-linked detail page has no directory entry behind it, and an
 *     org reached through a contact page has the contact behind it.
 *   - the SCROLL POSITION for that search — handed back exactly once,
 *     because the directory renders its results after it mounts, so the
 *     browser's own restoration fires before there is anything to
 *     scroll to.
 *
 * sessionStorage, not state: the handoff crosses a route change, and the
 * value must survive a reload of the tab. It is tab-scoped and dies with
 * the tab. Every access is guarded — a browser that refuses storage
 * (private mode, quota) degrades to the empty-directory fallback, never
 * to an error.
 */

const SEARCH_KEY = 'peaceos.directory.return';
const SCROLL_KEY = 'peaceos.directory.scroll';

/** Only a same-app directory URL is ever remembered or followed. */
const DIRECTORY_URL = /^\/t\/[^/?#]+\/directory(\?[^#]*)?$/;

export function rememberDirectorySearch(url: string): void {
  if (!DIRECTORY_URL.test(url)) return;
  try {
    window.sessionStorage.setItem(SEARCH_KEY, url);
  } catch {
    // Storage refused: the fallback directory still works.
  }
}

/** The last remembered directory URL for this tab, or null. */
export function readDirectorySearch(): string | null {
  try {
    const raw = window.sessionStorage.getItem(SEARCH_KEY);
    return raw !== null && DIRECTORY_URL.test(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function rememberDirectoryScroll(search: string, y: number): void {
  try {
    window.sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ search, y }));
  } catch {
    // Storage refused: the reader starts at the top.
  }
}

/**
 * The scroll position saved against exactly this search, taken (cleared)
 * on read so it applies to one arrival only. null when none was saved,
 * or when it was saved against a different search.
 */
export function takeDirectoryScroll(search: string): number | null {
  try {
    const raw = window.sessionStorage.getItem(SCROLL_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      (parsed as { search?: unknown }).search !== search ||
      typeof (parsed as { y?: unknown }).y !== 'number'
    ) {
      // Saved against another search (or unreadable): leave it for the
      // arrival it belongs to.
      return null;
    }
    window.sessionStorage.removeItem(SCROLL_KEY);
    return (parsed as { y: number }).y;
  } catch {
    return null;
  }
}
